import { groupBy, some } from 'lodash';
import { useMemo } from 'react';
import { Badge, badgeClasses, Box, ButtonBase, Divider, NewMenu, Stack, Tooltip, Typography } from '@common-components';
import { KeyboardArrowDown as KeyboardArrowDownIcon, PersonOutline as PersonIcon } from '@icons';
import { useOptionsMenu } from 'hooks';
import { messages } from 'i18n';
import { iconFontSize } from 'themes';

import { User } from 'types';
import { uniteStyles } from 'utils';
import { MenuGroup } from 'components/menu';

interface TeammatesCcGroupContactsProps {
  teammates: User[];
  selectedTeammates: User[];
  toggleTeammate?: (value: User) => void;
  isMenu?: boolean;
  spacingBottom?: number;
  disabled?: boolean;
  label?: string;
}

const MarketStyles = {
  border: 1,
  borderColor: 'divider',
  px: 1,
  py: '2px',
  borderRadius: '20px',
  maxWidth: 1,
  alignItems: 'center',
  backgroundColor: 'common.white',
  '&:hover': { backgroundColor: 'blueGrey.100' },
} as const;

export default function TeammatesCcGroupContacts({
  teammates,
  isMenu,
  selectedTeammates,
  toggleTeammate,
  spacingBottom = 1,
  disabled,
  label = messages.emailEditor.cc,
}: TeammatesCcGroupContactsProps) {
  const optionsMenu = useOptionsMenu();

  const teammatesByTeam = useMemo(() => groupBy(teammates, 'teamId'), [teammates]);

  const menuGroups: MenuGroup[] = useMemo(() => {
    const teamIds = Object.keys(teammatesByTeam);
    if (teamIds.length === 0) {
      return [];
    }

    return teamIds.map((teamId) => {
      const teamUsers = teammatesByTeam[teamId];
      const teamName = teamUsers[0]?.team.name || 'Team';

      return {
        key: `team-${teamId}-menu`,
        header: teamName,
        selectionType: 'checkbox',
        itemsLimit: teamUsers.length > 5 ? 5 : teamUsers.length,
        items: teamUsers.map((teammate) => ({
          label: `${teammate.firstName} ${teammate.lastName}`,
          onClick: () => toggleTeammate?.(teammate),
          tooltipContent: teammate.email,
          tooltipPlacement: 'right',
          isSelected: some(selectedTeammates, { id: teammate.id }),
        })),
      };
    });
  }, [teammatesByTeam, selectedTeammates, toggleTeammate]);

  const contactsListString = useMemo(
    () => teammates.map((teammate) => `${teammate.firstName} ${teammate.lastName}`).join(', '),
    [teammates],
  );

  return (
    <Box>
      <ButtonBase
        disabled={disabled}
        aria-label="market menu"
        onClick={optionsMenu.openMenu}
        color="inherit"
        sx={{ p: 0, mb: spacingBottom, borderRadius: 4 }}
      >
        <Tooltip tooltipContent={isMenu ? '' : contactsListString} placement="right">
          <Stack
            direction="row"
            sx={uniteStyles(MarketStyles, {
              borderRadius: 4,
            })}
          >
            <PersonIcon sx={{ color: 'text.secondary', fontSize: iconFontSize }} />
            <Typography noWrap variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
              {label}
            </Typography>
            <Divider variant="fullWidth" sx={{ mx: 1, my: '-2px' }} flexItem orientation="vertical" />
            <Badge
              badgeContent={selectedTeammates.length}
              showZero
              sx={{
                [`& .${badgeClasses.badge}`]: {
                  position: 'relative',
                  transform: 'inherit',
                  backgroundColor: 'blueGrey.100',
                },
              }}
            />
            {toggleTeammate && <KeyboardArrowDownIcon sx={{ ml: '2px' }} />}
          </Stack>
        </Tooltip>
      </ButtonBase>
      {toggleTeammate && <NewMenu optionsMenuState={optionsMenu} menuItems={menuGroups} />}
    </Box>
  );
}
